import { useCollectionPageHref } from "@/app/navigation/collection"
import { CollectionPageType } from "@/app/navigation/collection/collection-page-type"
import type { CollectionId } from "@anaconda/core/nft-collection/types"
import type { Nft, TraitWithRarity } from "@anaconda/core/nft/types"
import { Card, Skeleton } from "@rarible_int/anaconda_ui_components"
import Link from "next/link"
import { useMemo } from "react"
import { applyFiltersToSearchQuery } from "../../search/filters/store"

interface Props {
  trait: TraitWithRarity
  collectionId: CollectionId
}

export function Trait({ trait, collectionId }: Props) {
  const rarity = formatTraitRarityPercentage(Number(trait.rarity))

  const traitsFilter = useMemo(
    () => ({ traits: [{ key: trait.key, values: [trait.value] }] }),
    [trait],
  )

  const baseHref = useCollectionPageHref(collectionId, CollectionPageType.ITEMS)
  const searchParams = applyFiltersToSearchQuery(
    traitsFilter,
    new URLSearchParams(),
  )
  const href = `${baseHref}?${searchParams.toString()}`

  return (
    <Link href={href}>
      <TraitLayout
        name={<span className="text-foreground/60 text-xs">{trait.key}</span>}
        value={trait.value}
        rarity={<span className="text-foreground/60">{rarity}</span>}
      />
    </Link>
  )
}

function formatTraitRarityPercentage(value: number) {
  if (!Number.isFinite(value)) return "—"
  if (value === 0) return "0%"
  if (Math.abs(value) < 1) return `${value.toFixed(4)}%`

  return `${value.toFixed(1)}%`
}

export function TraitSkeleton() {
  return (
    <TraitLayout
      name={<Skeleton className="h-4 w-[70px]" />}
      value={<Skeleton className="h-6 w-[75px]" />}
      rarity={<Skeleton className="h-6 w-[40px]" />}
    />
  )
}

function TraitLayout(props: {
  name: React.ReactNode
  value: React.ReactNode
  rarity: React.ReactNode
}) {
  return (
    <Card className="flex flex-col gap-[2px] text-nowrap rounded-md p-4 transition-colors duration-200 hover:cursor-pointer hover:bg-white/5">
      <span className="overflow-hidden text-ellipsis text-xs">
        {props.name}
      </span>
      <div className="flex items-center justify-between gap-[2px]">
        <span className="overflow-hidden text-ellipsis">{props.value}</span>
        {props.rarity}
      </div>
    </Card>
  )
}
